import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ApplicationState from "@SupplyLand/Store/ApplicationState";
import { getDisplayBreakPrices } from "@SupplyLand/Utilities/minimumOrderBreaks";
import { getSalePriceDisplay } from "@SupplyLand/Utilities/salePricing";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState, ownProps: HasProduct) => ({
    configurationSelection: state.pages.productDetails.configurationSelection,
    configurationCompleted: state.pages.productDetails.configurationCompleted,
    variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
    subscription: state.pages.productDetails.subscription,
});

export interface ProductDetailsPageTitleStyles {
    container?: GridContainerProps;
    titleGridItem?: GridItemProps;
    checkoutButton?: ButtonPresentationProps;
    buyNowButton?: ButtonPresentationProps;
    checkoutButtonGridItem?: GridItemProps;
    defaultButtonGridItem?: GridItemProps;
    button?: ButtonPresentationProps;
    table?: DataTableProps;
    tableHead?: DataTableHeadProps;
    breakQtyHeader?: DataTableHeaderProps;
    breakPriceHeader?: DataTableHeaderProps;
    savingsHeader?: DataTableHeaderProps;
    clearanceHeader?: DataTableHeaderProps;
    tableBody?: DataTableBodyProps;
    tableRow?: DataTableRowProps;
    breakQtyCell?: DataTableCellBaseProps;
    breakPriceCell?: DataTableCellBaseProps;
    savingsCell?: DataTableCellBaseProps;
    clearanceCell?: DataTableCellBaseProps;
    slashedPrice?: InjectableCss;
}

// The table sits in the page's right-hand column and gains a third column when a product is on sale.
// Mobius cells default to nowrap with 12px side padding, which pushes that column past the edge of the
// column at desktop widths, so the table is fixed-layout at full width and cells wrap and pad tighter.
const tableCellCss = css`
    height: 30px;
    padding: 4px 6px;
    white-space: normal;
    line-height: 1.2;
    font-size: 14px;
`;

// The theme's data table header typography uses a fixed 15px size; inherit from the header cell so the
// cell font size above applies.
const headerTypographyProps: TypographyPresentationProps = {
    css: css`
        font-size: inherit;
        line-height: inherit;
    `,
};

export const pageTitleStyles: ProductDetailsPageTitleStyles = {
    container: {
        gap: 0,
        offsetCss: css`
            max-width: 100%;
        `,
    },
    titleGridItem: {
        width: 12,
        css: css`
            padding-bottom: 20px;
        `,
    },
    checkoutButtonGridItem: {
        width: 12,
        css: css`
            font-size: 26px;
            font-weight: 600;
            label {
                font-size: 26px;
                font-weight: 600;
            }
        `,
    },
    defaultButtonGridItem: {
        width: 12,
        css: css`
            font-size: 26px;
            font-weight: 600;
            label {
                font-size: 26px;
                font-weight: 600;
            }
        `,
    },
    checkoutButton: {
        css: css`
            color: black;
            /// box-shadow: 2px 2px 2px rgb(0 0 0 / 60%);
            background: ${getColor("primary")};
            width: 100%;
            margin-top: 10px;
            text-transform: uppercase;
            border: 0px;
            height: 55px;
        `,
    },
    buyNowButton: {
        css: css`
            color: white;
            background: black;
            width: 100%;
            margin-top: 10px;
            text-transform: uppercase;
            border: 0px;
            height: 50px;
        `,
    },
    button: {
        css: css`
            color: black;
            background: ${getColor("primary")};
            width: 100%;
            margin-top: 10px;
            text-transform: uppercase;
            border: 0px;
            height: 50px;
        `,
    },
    table: {
        css: css`
            width: 100%;
            table-layout: fixed;
        `,
    },
    breakQtyHeader: {
        alignX: "center",
        typographyProps: headerTypographyProps,
        css: css`
            width: 26%;
            ${tableCellCss}
        `,
    },
    breakPriceHeader: {
        alignX: "center",
        typographyProps: headerTypographyProps,
        css: tableCellCss,
    },
    savingsHeader: {
        alignX: "center",
        typographyProps: headerTypographyProps,
        css: tableCellCss,
    },
    breakQtyCell: {
        alignX: "center",
        css: tableCellCss,
    },
    breakPriceCell: {
        alignX: "center",
        css: tableCellCss,
    },
    savingsCell: {
        alignX: "center",
        css: tableCellCss,
    },
    // Same treatment as the product list table (Overrides/Widgets/ProductList/ProductListActions.tsx):
    // regular prices struck through, the sale price in a red "Clearance" column.
    clearanceHeader: {
        alignX: "center",
        typographyProps: headerTypographyProps,
        css: css`
            ${tableCellCss}
            color: #FF0000;
            font-weight: 700;
        `,
    },
    clearanceCell: {
        alignX: "center",
        css: css`
            ${tableCellCss}
            color: #FF0000;
            font-weight: 600;
        `,
    },
    slashedPrice: {
        css: css`
            display: inline-block;
            text-decoration: line-through;
            color: #6b6b6b;
        `,
    },
};

const styles = pageTitleStyles;

const ProductDetailsPageAction = ({
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    product,
    productInfo,
}: Props) => {
    const pricing = productInfo?.pricing;
    const breakPrices = getDisplayBreakPrices(pricing?.unitRegularBreakPrices, product, pricing?.unitOfMeasure);
    // The admin sale price only exists in the scalar pricing fields, not in the break arrays, so when
    // the product is on sale the one sale price is shown against every break, as the product list does.
    const salePriceDisplay = getSalePriceDisplay(pricing);
    return (
        <GridContainer {...styles.container}>
            {pricing?.unitOfMeasure && (
                <GridItem width={12}>
                    <Typography weight={600}>Sold in {pricing!.unitOfMeasure} quantities</Typography>
                </GridItem>
            )}
            {breakPrices.length > 0 && (
                <GridItem {...styles.titleGridItem}>
                    <DataTable {...styles.table}>
                        <DataTableHead {...styles.tableHead}>
                            <DataTableHeader {...styles.breakQtyHeader}>{translate("Qty Breaks")}</DataTableHeader>
                            <DataTableHeader {...styles.breakPriceHeader}>{translate("Price Per")} {pricing?.unitOfMeasure}</DataTableHeader>
                            {salePriceDisplay && (
                                <DataTableHeader {...styles.clearanceHeader}>{translate("Clearance")}</DataTableHeader>
                            )}
                        </DataTableHead>
                        <DataTableBody {...styles.tableBody}>
                            {breakPrices.map(breakPrice => (
                                <DataTableRow
                                    {...styles.tableRow}
                                    key={breakPrice.breakQty}
                                    data-test-selector="breakPriceRow"
                                >
                                    <DataTableCell {...styles.breakQtyCell} data-test-selector="breakQty">
                                        {breakPrice.breakQty}
                                    </DataTableCell>
                                    <DataTableCell {...styles.breakPriceCell} data-test-selector="price">
                                        {salePriceDisplay ? (
                                            <StyledWrapper {...styles.slashedPrice}>
                                                <span
                                                    dangerouslySetInnerHTML={{ __html: breakPrice.breakPriceDisplay || "" }}
                                                />
                                            </StyledWrapper>
                                        ) : (
                                            <span dangerouslySetInnerHTML={{ __html: breakPrice.breakPriceDisplay || "" }} />
                                        )}
                                    </DataTableCell>
                                    {salePriceDisplay && (
                                        <DataTableCell {...styles.clearanceCell} data-test-selector="clearancePrice">
                                            <span dangerouslySetInnerHTML={{ __html: salePriceDisplay }} />
                                        </DataTableCell>
                                    )}
                                </DataTableRow>
                            ))}
                        </DataTableBody>
                    </DataTable>
                </GridItem>
            )}
            <GridItem {...styles.checkoutButtonGridItem}>
                <ProductAddToCartButton
                    configurationSelection={configurationSelection}
                    configurationCompleted={configurationCompleted}
                    variantSelectionCompleted={variantSelectionCompleted}
                    subscription={subscription}
                    extendedStyles={styles.button}
                    data-test-selector="addProductToCart"
                />
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps)(ProductDetailsPageAction)),
    definition: {
        displayName: "Product Details Action Bottom",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
